import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Search,
  Clock,
  Truck,
  Radio,
  ClipboardCheck,
  ShieldAlert,
  MapPinned,
  Boxes,
  Route,
  Headphones,
  ClipboardList,
  Layers,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";
import Seo, { buildBreadcrumbs } from "@/components/Seo";

// ─────────────────────────────────────────────────────────────
// Editorial layout: an operations DOSSIER.
//   Left rail = clickable table-of-contents (with active section)
//   Right column = numbered dossier chapters, each with a bullet
//   grid of concrete deliverables.
// Deliberately distinct from the "guest journey" narrative used on
// HotelTravel, and from the numbered cards used elsewhere.
// ─────────────────────────────────────────────────────────────

const DOSSIER = [
  {
    id: "recon",
    ref: "§01",
    label: "Site Recon",
    title: "Recon the venue before contracts get signed.",
    icon: Search,
    body: "Load-in doors, service corridors, power drops, sight lines. We walk the room before you commit — with a two-page site report and a red-flag list in your inbox 48 hours later.",
    items: [
      "Site inspection & load-in walk-through",
      "Room capacity & fire-code validation",
      "Power, rigging, and network audit",
      "Loading dock & freight elevator survey",
      "ADA & accessibility path mapping",
      "48-hour red-flag report",
    ],
  },
  {
    id: "timeline",
    ref: "§02",
    label: "Timeline",
    title: "One timeline. Every stakeholder. One truth.",
    icon: Clock,
    body: "Run-of-show, load-in plan, and rehearsal grid — all versioned, all timestamped, all in a shared workspace. Vendors work off the same beat as your executive team.",
    items: [
      "Master run-of-show (minute-by-minute)",
      "Load-in / strike timeline",
      "Rehearsal & cue-to-cue schedule",
      "Speaker & VIP itinerary sheets",
      "Live change-log with owner + timestamp",
      "Print-ready pocket schedules for staff",
    ],
  },
  {
    id: "vendors",
    ref: "§03",
    label: "Vendors",
    title: "Contract discipline — from RFP to sign-off.",
    icon: ClipboardList,
    body: "Every vendor scoped, quoted, and held to a service-level standard. Change orders documented, insurance certificates on file, and one point of accountability on our side.",
    items: [
      "RFPs · A/V, catering, staffing, security",
      "Bid comparisons with negotiated terms",
      "COI & insurance verification",
      "Change-order tracking & approvals",
      "Vendor call-sheets & load-in bibles",
      "Food & Beverage coordination",
      "Post-event vendor scorecards",
    ],
  },
  {
    id: "freight",
    ref: "§04",
    label: "Logistics & Execution",
    title: "Boxes in, badges on — attendee flow architected end-to-end.",
    icon: Truck,
    body: "Signage plans, wayfinding, freight tracking, travel confirmations, and attendee-flow modeling — so the room breathes at scale and nobody backs up at the door.",
    items: [
      "Freight, signage & shipping consolidation",
      "Signage & wayfinding plans (printed + digital)",
      "Attendee-flow & queue modeling",
      "Coat check, storage, and lost-and-found ops",
      "Show-floor drayage & load-in staging",
      "Wrap up — Air, hotel and ground accommodations",
      "Trash, recycling & sustainability plan",
    ],
  },
  {
    id: "onsite",
    ref: "§05",
    label: "On-Site Ops",
    title: "A radio net on the floor — and a director in the wings.",
    icon: Radio,
    body: "Certified directors on communications, a live production control desk, and pre-scripted contingency binders. When something moves, we already had a playbook for it.",
    items: [
      "Radio net · production, security, transport",
      "Production control desk & show-caller",
      "Contingency playbook (weather, medical, IT)",
      "Backstage & green-room management",
      "Registration desk & credential control",
      "Stress-free on-site leadership and execution",
      "Nightly ops debrief + next-day pivots",
    ],
  },
  {
    id: "wrap",
    ref: "§06",
    label: "Wrap & Report",
    title: "Strike is a discipline. So is the debrief.",
    icon: ShieldAlert,
    body: "Bags out, floors cleared, invoices reconciled. Then a full post-mortem — what to repeat, what to retire — delivered in one packaged report.",
    items: [
      "Strike & load-out coordination",
      "Vendor final-billing reconciliation",
      "Damage / incident documentation",
      "Post-event survey aggregation",
      "Executive read-out deck",
      "Playbook refresh for next program",
    ],
  },
];

// Small hook: highlights the ToC entry closest to the top of the viewport.
function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const handler = () => {
      const scrollY = window.scrollY + 200;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollY) current = id;
      }
      setActive(current);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [ids]);
  return active;
}

const OPERATING_PRINCIPLES = [
  {
    n: "01",
    title: "One accountable owner per line.",
    body: "Every deliverable has a name next to it. Vendor drops the ball? We do, too.",
  },
  {
    n: "02",
    title: "Documented before it happens.",
    body: "Playbooks, run-sheets, and change logs — versioned, signed, distributed.",
  },
  {
    n: "03",
    title: "Radio-tested. Rehearsed.",
    body: "Comms, cues, and contingencies drilled the day before doors open — always.",
  },
  {
    n: "04",
    title: "Debrief is the deliverable.",
    body: "The last page of the report is the first page of the next program.",
  },
];

export default function LogisticsPlanning() {
  const sectionIds = DOSSIER.map((d) => d.id);
  const active = useActiveSection(sectionIds);

  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="Logistics Planning — Blueprint-Grade Event Operations"
        description="Site recon, run-of-show, freight, vendor management, and on-site production control — engineered as one operating document. For associations, conferences, and executive programs."
        path="/services/logistics-planning"
        keywords="event logistics planning, run of show, event operations management, vendor management, load-in planning, event production control, conference logistics"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Event Logistics Planning",
            serviceType: "Event Operations & Logistics",
            provider: { "@type": "Organization", name: "Magee Enterprises" },
            areaServed: "Global",
            description:
              "Blueprint-grade logistics: site recon, run-of-show, vendor discipline, logistics, and on-site production control for corporate events, conferences, and executive programs.",
          },
          buildBreadcrumbs([
            ["Home", "/"],
            ["Services", "/services"],
            ["Logistics Planning", "/services/logistics-planning"],
          ]),
        ]}
      />

      {/* ============ HERO (blueprint feel) ============ */}
      <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-blue w-[560px] h-[560px] -top-32 -left-32 opacity-35" />
        <HexMark
          size={260}
          opacity={0.055}
          className="absolute -right-8 top-28 rotate-[16deg] pointer-events-none hidden md:block"
        />
        <span className="corner-cross" style={{ top: 40, left: 40 }} />
        <span className="corner-cross" style={{ top: 40, right: 40 }} />
        <span className="corner-cross" style={{ bottom: 40, left: 40 }} />
        <span className="corner-cross" style={{ bottom: 40, right: 40 }} />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-4 mb-10 flex-wrap">
              <span className="w-10 h-px bg-[var(--text)]/40" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text-muted)]">
                Logistics Planning · Operating Dossier
              </span>
              <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-700 border border-cyan-500/30 rounded-full px-2.5 py-1 bg-cyan-500/5">
                v · 2026.02
              </span>
            </div>
            <MaskedReveal
              lines={["The blueprint", <React.Fragment key="line2">behind the <span className="text-cyan-600">room.</span></React.Fragment>]}
              className="font-editorial tracking-[-0.03em] leading-[0.94] text-[13vw] md:text-[7.4vw] lg:text-[6.4vw]"
              stagger={0.09}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.35}>
              <p className="text-[var(--text)]/75 text-lg leading-relaxed">
                Site recon, run-of-show, vendor discipline, logistics, and a
                production control desk — architected as a single operating
                document, not a stack of loose deliverables.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="btn-primary"
                  data-testid="logistics-hero-cta-quote"
                >
                  Request a proposal <ArrowRight size={14} />
                </Link>
                <a
                  href="#dossier"
                  className="btn-dark"
                  data-testid="logistics-hero-cta-explore"
                >
                  Open the dossier <ArrowUpRight size={14} />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ HERO IMAGE ============ */}
      <section className="relative pb-16 md:pb-24 -mt-8 md:-mt-12">
        <div className="max-w-[1600px] mx-auto px-4 md:px-10">
          <Reveal>
            <figure className="relative rounded-3xl overflow-hidden border border-[var(--border)] shadow-[0_30px_80px_-40px_rgba(6,60,120,0.45)]">
              <img
                src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1920&q=80"
                alt="Event production control desk — logistics planning by Magee Enterprises"
                loading="lazy"
                className="w-full h-[42vh] md:h-[60vh] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
              <figcaption className="absolute bottom-5 left-5 md:bottom-8 md:left-10 max-w-md">
                <span className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300 bg-black/40 backdrop-blur px-2.5 py-1 rounded-full border border-cyan-300/30 inline-block">
                  On-site production control · Radio net live
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>
      <section
        id="dossier"
        className="relative py-20 md:py-32 overflow-hidden scroll-mt-28 bg-[var(--surface)]"
      >
        <div className="absolute inset-0 hex-pattern-dense opacity-40 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[440px] h-[440px] -bottom-24 -right-24 opacity-30" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
              <div>
                <span className="chip">
                  <Layers size={12} /> Dossier · 6 sections
                </span>
                <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Every discipline —{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    documented, signed, delivered.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                Scroll through the operating dossier. Each section is what
                actually goes into your program plan — nothing marketing-facing.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {/* ── Left rail: sticky ToC ── */}
            <aside className="col-span-12 lg:col-span-3">
              <div className="lg:sticky lg:top-28">
                <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mb-4 hidden lg:block">
                  Table of contents
                </div>
                <nav
                  data-testid="dossier-toc"
                  className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-2 md:gap-3"
                >
                  {DOSSIER.map((s) => {
                    const isActive = active === s.id;
                    return (
                      <a
                        key={s.id}
                        href={`#${s.id}`}
                        data-testid={`dossier-toc-${s.id}`}
                        aria-current={isActive ? "true" : undefined}
                        className={`group flex items-center gap-3 rounded-lg px-3 py-2.5 border transition-all ${
                          isActive
                            ? "bg-white border-[var(--accent)]/40 shadow-[0_10px_30px_-18px_rgba(6,182,212,0.5)]"
                            : "bg-transparent border-transparent hover:border-[var(--border)] hover:bg-white/60"
                        }`}
                      >
                        <span
                          className={`font-mono-brand text-[10px] tracking-[0.22em] ${
                            isActive ? "text-[var(--accent)]" : "text-[var(--text-muted)]"
                          }`}
                        >
                          {s.ref}
                        </span>
                        <span
                          className={`text-[13px] leading-tight ${
                            isActive
                              ? "text-[var(--text)]"
                              : "text-[var(--text)]/70 group-hover:text-[var(--text)]"
                          }`}
                        >
                          {s.label}
                        </span>
                      </a>
                    );
                  })}
                </nav>
              </div>
            </aside>

            {/* ── Right column: chapters ── */}
            <div className="col-span-12 lg:col-span-9 space-y-6 md:space-y-8">
              {DOSSIER.map((s, i) => {
                const isFlagship = s.id === "onsite";
                return (
                <Reveal key={s.id} delay={i * 0.04}>
                  <article
                    id={s.id}
                    data-testid={`dossier-section-${s.id}`}
                    className={`relative p-6 md:p-10 scroll-mt-28 overflow-hidden rounded-2xl transition-all duration-500 ${
                      isFlagship
                        ? "bg-gradient-to-br from-[#2E3641] via-[#3B4552] to-[#2E3641] text-white border border-cyan-400/25 shadow-[0_30px_80px_-30px_rgba(6,182,212,0.35)]"
                        : "card-white"
                    }`}
                  >
                    {/* Flagship-only creative flair */}
                    {isFlagship && (
                      <>
                        {/* Animated cyan corner glow */}
                        <div className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full bg-cyan-400/18 blur-[90px] pointer-events-none" />
                        <div className="absolute -bottom-24 -left-24 w-[320px] h-[320px] rounded-full bg-sky-400/15 blur-[80px] pointer-events-none" />

                        {/* Dashed grid texture */}
                        <div
                          className="absolute inset-0 opacity-[0.08] pointer-events-none"
                          style={{
                            backgroundImage:
                              "linear-gradient(rgba(6,182,212,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.6) 1px, transparent 1px)",
                            backgroundSize: "48px 48px",
                          }}
                        />

                        {/* Signature ribbon */}
                        <div
                          data-testid="dossier-flagship-ribbon"
                          className="absolute top-6 right-6 z-10 rotate-3"
                        >
                          <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-sky-500 text-[#02050B] px-3 py-1.5 shadow-[0_10px_30px_-10px_rgba(6,182,212,0.9)]">
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/70" />
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                            </span>
                            <span className="font-mono-brand text-[9.5px] uppercase tracking-[0.28em] font-bold">
                              Signature · What we do best
                            </span>
                          </div>
                        </div>

                        {/* Subtle animated bracket on the left */}
                        <span className="absolute left-0 top-6 bottom-6 w-[3px] rounded-full bg-gradient-to-b from-transparent via-cyan-400 to-transparent" />
                      </>
                    )}
                    <span className={`corner-cross ${isFlagship ? "!text-cyan-300" : ""}`} style={{ top: 14, right: 14 }} />
                    <div className="relative grid grid-cols-12 gap-4 md:gap-6">
                      <div className="col-span-12 md:col-span-4 flex flex-col">
                        <div className="flex items-center gap-3">
                          <div className={`icon-tile ${isFlagship ? "!bg-cyan-400/15 !border-cyan-300/30 !text-cyan-300" : ""}`}>
                            <s.icon size={20} />
                          </div>
                          <div className={`numeral-mono text-3xl md:text-5xl select-none ${isFlagship ? "text-white" : ""}`}>
                            {s.ref}
                          </div>
                        </div>
                        <div className={`mt-5 font-mono-brand text-[10px] uppercase tracking-[0.24em] ${isFlagship ? "text-cyan-300" : "text-[var(--text-muted)]"}`}>
                          Section · {s.label}
                        </div>
                        <h3 className={`mt-2 font-editorial leading-[1.05] tracking-[-0.02em] ${isFlagship ? "text-3xl md:text-4xl text-white" : "text-2xl md:text-3xl"}`}>
                          {isFlagship ? (
                            <>
                              A radio net on the floor — and a{" "}
                              <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium">
                                director in the wings.
                              </span>
                            </>
                          ) : (
                            s.title
                          )}
                        </h3>
                      </div>

                      <div className={`col-span-12 md:col-span-8 md:pl-6 md:border-l ${isFlagship ? "md:border-cyan-400/25" : "md:border-[var(--border)]"}`}>
                        <p className={`leading-relaxed max-w-2xl ${isFlagship ? "text-white/85 text-[16.5px]" : "text-[var(--text-muted)]"}`}>
                          {s.body}
                        </p>
                        <div className={`mt-6 font-mono-brand text-[10px] uppercase tracking-[0.24em] mb-3 ${isFlagship ? "text-cyan-300" : "text-[var(--text-muted)]"}`}>
                          Deliverables
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                          {s.items.map((d) => (
                            <li
                              key={d}
                              className={`flex items-start gap-3 text-[14px] leading-snug ${isFlagship ? "text-white/85" : "text-[var(--text)]/85"}`}
                            >
                              <span className={`mt-1.5 shrink-0 inline-block w-1.5 h-1.5 rounded-full ${isFlagship ? "bg-cyan-300 shadow-[0_0_10px_rgba(103,232,249,0.9)]" : "bg-gradient-to-br from-cyan-400 to-sky-600"}`} />
                              <span>{d}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </article>
                </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ OPERATING PRINCIPLES (dark strip) ============ */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-[#050A14] text-white">
        <div className="absolute inset-0 grid-pattern-dark opacity-30 pointer-events-none" />
        <div className="absolute -top-32 -left-32 w-[440px] h-[440px] rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="absolute -bottom-24 -right-16 w-[520px] h-[520px] rounded-full bg-sky-500/15 blur-3xl" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="mb-14 md:mb-20">
              <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                <Radio size={12} /> Operating Principles
              </span>
              <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                The four rules that make{" "}
                <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-cyan-400 bg-clip-text text-transparent italic font-medium">
                  the day feel inevitable.
                </span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {OPERATING_PRINCIPLES.map((p) => (
              <Reveal key={p.n}>
                <div className="relative border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] rounded-2xl p-6 md:p-8 h-full transition-all duration-500">
                  <span className="corner-cross" style={{ top: 12, right: 12, color: "#fff" }} />
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300 mb-4">
                    Principle · {p.n}
                  </div>
                  <h3 className="font-editorial text-2xl md:text-3xl leading-[1.05] tracking-[-0.02em]">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-white/65 text-[14.5px] leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CLOSING CTA ============ */}
      <section className="relative py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-white relative overflow-hidden p-8 md:p-16">
            <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
            <div className="brand-bloom brand-bloom-cyan w-[380px] h-[380px] -top-32 -left-32 opacity-40" />
            <div className="brand-bloom brand-bloom-blue w-[420px] h-[420px] -bottom-32 -right-32 opacity-30" />
            <div className="relative grid grid-cols-12 gap-8 items-end">
              <div className="col-span-12 lg:col-span-8">
                <span className="chip">
                  <ClipboardCheck size={12} /> Booking Q2 &ndash; Q4 2026
                </span>
                <h3 className="mt-6 font-editorial text-3xl md:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.03em]">
                  Bring us your program.{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    We&apos;ll bring the dossier.
                  </span>
                </h3>
                <p className="mt-6 text-[var(--text-muted)] max-w-xl leading-relaxed">
                  A scoped conversation within one business day. Full-service,
                  retained, or embedded — your call.
                </p>
              </div>
              <div className="col-span-12 lg:col-span-4 flex flex-col sm:flex-row lg:justify-end gap-3">
                <Link to="/contact" className="btn-primary justify-center sm:justify-start w-full sm:w-auto" data-testid="logistics-footer-cta-quote">
                  Request a proposal <ArrowRight size={14} />
                </Link>
                <Link to="/contact#book" className="btn-dark justify-center sm:justify-start w-full sm:w-auto" data-testid="logistics-footer-cta-book">
                  Book a call <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
